import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Stack
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';

/**
 * Bug Report Form
 * Form for reporting bugs with detailed information
 */
const BugReportForm = ({ onSubmit, onCancel, isSubmitting }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    reproduction_steps: '',
    expected_behavior: '',
    actual_behavior: ''
  });

  const [errors, setErrors] = useState({});

  const categories = [
    'Account Sync',
    'UI/UX',
    'Performance',
    'Security',
    'Transaction Categorization',
    'Budget Tools',
    'Reports',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title || formData.title.trim().length === 0) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 255) {
      newErrors.title = 'Title must be less than 255 characters';
    }

    if (!formData.description || formData.description.trim().length === 0) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 20) {
      newErrors.description = 'Description must be at least 20 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    onSubmit(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={3}>
        {/* Title */}
        <TextField
          fullWidth
          required
          label="Bug Title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          error={!!errors.title}
          helperText={errors.title || 'Brief description of the issue'}
          placeholder="e.g., Unable to sync Chase account"
          inputProps={{ maxLength: 255 }}
          disabled={isSubmitting}
        />

        {/* Category */}
        <FormControl fullWidth>
          <InputLabel>Category</InputLabel>
          <Select
            name="category"
            value={formData.category}
            onChange={handleChange}
            label="Category"
            disabled={isSubmitting}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {categories.map(cat => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Description */}
        <TextField
          fullWidth
          required
          multiline
          rows={4}
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          error={!!errors.description}
          helperText={errors.description || 'Describe what happened, what you expected, and steps to reproduce'}
          placeholder="Please provide as much detail as possible..."
          disabled={isSubmitting}
        />

        {/* Optional fields */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: -1 }}>
          Optional Details (helps us fix the bug faster):
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={2}
          label="Steps to Reproduce"
          name="reproduction_steps"
          value={formData.reproduction_steps}
          onChange={handleChange}
          placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
          disabled={isSubmitting}
        />

        <TextField
          fullWidth
          multiline
          rows={2}
          label="Expected Behavior"
          name="expected_behavior"
          value={formData.expected_behavior}
          onChange={handleChange}
          placeholder="What did you expect to happen?"
          disabled={isSubmitting}
        />

        <TextField
          fullWidth
          multiline
          rows={2}
          label="Actual Behavior"
          name="actual_behavior"
          value={formData.actual_behavior}
          onChange={handleChange}
          placeholder="What actually happened?"
          disabled={isSubmitting}
        />

        {/* TODO: Screenshot upload component (Phase 2 continuation) */}
        <Typography variant="caption" color="text.secondary">
          Screenshot upload coming soon...
        </Typography>

        {/* Action buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SendIcon />}
            disabled={isSubmitting}
          >
            Submit Bug Report
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default BugReportForm;

